<script setup lang="ts">
import { formatDateTime } from '@metapic-apps/shared'
import { computed } from 'vue'

import { getCreatorReportHtmlUrl } from '@/api'
import type { CreatorReportItem, CreatorReportStatus } from '@/lib/adapters/creator-report.adapter'
import { BoxComponent, FlexComponent, TagComponent, TypographyComponent } from '@/ui-library'

const props = defineProps<{
  report: CreatorReportItem
}>()

const STATUS_LABELS: Record<CreatorReportStatus, string> = {
  pending: 'Generating…',
  completed: 'Completed',
  failed: 'Failed',
}

const STATUS_TYPES: Record<CreatorReportStatus, 'success' | 'warning' | 'danger'> = {
  pending: 'warning',
  completed: 'success',
  failed: 'danger',
}

const isCompleted = computed(() => props.report.status === 'completed')
const reportUrl = computed(() => getCreatorReportHtmlUrl(props.report.uuid))
</script>

<template>
  <BoxComponent :p="24" class="report-item">
    <FlexComponent justify="space-between" align="center" :gap="16">
      <FlexComponent direction="column" :gap="4">
        <TypographyComponent variant="title-large">Creator report</TypographyComponent>
        <TypographyComponent variant="body-small" color="var(--secondary-48)">
          Created {{ formatDateTime(report.createdAt) }}
        </TypographyComponent>
      </FlexComponent>

      <FlexComponent align="center" :gap="16">
        <TagComponent :type="STATUS_TYPES[report.status]">{{ STATUS_LABELS[report.status] }}</TagComponent>
        <a v-if="isCompleted" :href="reportUrl" target="_blank" rel="noopener noreferrer" class="report-item__link">
          <TypographyComponent variant="title-medium" color="var(--primary)">View report</TypographyComponent>
        </a>
      </FlexComponent>
    </FlexComponent>
  </BoxComponent>
</template>

<style scoped lang="scss">
.report-item {
  border-radius: 16px;
  border: 1px solid var(--button-hover);
  background: var(--inverted);
}

.report-item__link {
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
}
</style>
